/**
 * F12-P3 — "Solicitar dirigir un tipo de club" para cuentas EXISTENTES.
 *
 * El único flujo que creaba solicitudes `section_director` vivía en el wizard
 * de registro, inalcanzable para quien ya tiene cuenta y membresía. Resultado:
 * directores reales entrando como miembros rasos ("no me deja hacer nada") y
 * 94 secciones activas sin director en producción — cada caso se resolvía por
 * WhatsApp. Este componente abre esa puerta reusando TODO el backend que ya
 * existe: la solicitud cae en el mismo buzón (`/organization/director-requests`)
 * y al aprobarse la CF asigna el cargo.
 *
 * Solo ofrece secciones ACTIVAS y SIN director del club donde la persona ya es
 * miembro — no permite "quitarle" el puesto a nadie.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
} from "@/components/ui";
import { Shield, Loader2, CheckCircle2, Clock } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { toast } from "react-hot-toast";
import { useAuthStore } from "@/stores/authStore";
import { useMembersByLinkedUser, usePerson } from "@/hooks/usePersons";
import { joinRequestService } from "@/lib/services/joinRequests";
import { CLUB_TYPE_LABELS } from "@/types";
import type { Club, ClubType } from "@/types";
import type { JoinRequest } from "@/lib/services/joinRequests";

interface Vacancy {
  clubId: string;
  clubName: string;
  section: ClubType;
}

export default function RequestSectionDirectorCard() {
  const { profile } = useAuthStore();
  const { data: memberships } = useMembersByLinkedUser(profile?.id);
  const { data: person } = usePerson(profile?.personId ?? "");
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<JoinRequest[]>([]);
  const [sending, setSending] = useState<string | null>(null);

  const activeMemberships = useMemo(
    () => (memberships || []).filter((m) => m.status === "active"),
    [memberships],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!profile?.id) return;
      setLoading(true);
      try {
        // Solicitudes de dirección ya enviadas (para no ofrecer duplicados).
        const mine = await joinRequestService
          .getUserPendingRequest(profile.id)
          .catch(() => null);
        const pendingList = mine ? [mine] : [];

        const found: Vacancy[] = [];
        for (const m of activeMemberships) {
          const snap = await getDoc(doc(db, "clubs", m.clubId));
          if (!snap.exists()) continue;
          const club = snap.data() as Club;
          for (const [sec, cfg] of Object.entries(club.sections || {})) {
            if (cfg?.active && !cfg.directorUserId) {
              found.push({
                clubId: m.clubId,
                clubName: club.name ?? "Tu club",
                section: sec as ClubType,
              });
            }
          }
        }
        if (!cancelled) {
          setVacancies(found);
          setPending(pendingList);
        }
      } catch (e) {
        console.warn("[RequestSectionDirector] no se pudo cargar:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [profile?.id, activeMemberships]);

  const request = async (v: Vacancy) => {
    if (!profile || !person) {
      toast.error("Completa primero tus datos personales en tu perfil.");
      return;
    }
    const key = `${v.clubId}__${v.section}`;
    setSending(key);
    try {
      await joinRequestService.createJoinRequest({
        userId: profile.id,
        userEmail: profile.email,
        userName: person.fullName ?? profile.displayName,
        clubId: v.clubId,
        clubName: v.clubName,
        clubType: v.section,
        firstName: person.firstName ?? "",
        lastName: person.lastName ?? "",
        phone: person.phone ?? "",
        birthDate: person.birthDate,
        idDocumentType: person.idDocumentType,
        idDocumentNumber: person.idDocumentNumber,
        gender: person.gender,
        bloodType: person.bloodType,
        address: person.address ?? "",
        healthInsurance: person.healthInsurance ?? "",
        memberType: "lider",
        status: "pending",
        requestedRole: "section_director",
        requestedSection: v.section,
        requestedRoleNote:
          "Solicitud enviada desde el perfil por un miembro activo del club.",
      } as unknown as Parameters<typeof joinRequestService.createJoinRequest>[0]);
      toast.success(
        `Solicitud enviada. El director general de ${v.clubName} o la Asociación la revisará.`,
        { duration: 7000 },
      );
      setVacancies((prev) =>
        prev.filter((x) => `${x.clubId}__${x.section}` !== key),
      );
      setPending((p) => [...p, { requestedSection: v.section } as JoinRequest]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo enviar");
    } finally {
      setSending(null);
    }
  };

  // Nada que ofrecer: no ocupar espacio en el perfil.
  if (loading || (vacancies.length === 0 && pending.length === 0)) return null;

  const pendingDirector = pending.filter(
    (p) => p.requestedRole === "section_director" || p.requestedSection,
  );

  return (
    <Card className="border-0 shadow-md overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-gray-50/50 to-primary-50/30">
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="w-4 h-4 text-primary-600" />
          ¿Diriges un tipo de club?
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Si eres director/a de una de estas secciones, solicítalo aquí y tu
          director general (o la Asociación) lo confirma.
        </p>
      </CardHeader>
      <CardContent className="pt-5 space-y-2">
        {pendingDirector.length > 0 && (
          <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3">
            <Clock className="w-4 h-4 shrink-0" />
            Ya tienes una solicitud enviada, en espera de aprobación.
          </div>
        )}
        {vacancies.map((v) => {
          const key = `${v.clubId}__${v.section}`;
          return (
            <div
              key={key}
              className="flex items-center justify-between gap-3 border-2 border-gray-100 rounded-xl p-3"
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {CLUB_TYPE_LABELS[v.section] ?? v.section}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {v.clubName}
                  <Badge className="ml-2 bg-orange-100 text-orange-700">
                    sin director
                  </Badge>
                </p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                disabled={sending !== null || pendingDirector.length > 0}
                onClick={() => request(v)}
                className="shrink-0"
              >
                {sending === key ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                    Solicitar
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
